import { Link } from 'react-router-dom';
import { ArrowsLeftRightIcon, ArrowCounterClockwiseIcon, LockSimpleIcon, PauseIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SPACE_KIND_LABELS, spaceHref, spaceIcon } from '@/lib/spaces/navigation';
import { ruleLabel, ruleWindow } from '@/lib/spaces/rules';
import { cn } from '@/lib/utils';
import type { SpaceRuleOverview, SpaceRulesOverview, SpaceSendRule, SpaceSummary } from '@/types/spaces';
import { SpaceRulesPending } from './SpaceRulesPending';

interface Props {
  entry: SpaceRulesOverview;
  refreshing: boolean;
  changingRuleId: string | null;
  onToggle: (rule: SpaceSendRule) => Promise<void>;
}

export function SpaceRulesCard({ entry, refreshing, changingRuleId, onToggle }: Props) {
  const { space, rules } = entry;
  const Icon = spaceIcon(space.kind);
  const titleId = `rules-space-${space.id}`;
  const renderRule = (entry: SpaceRuleOverview) => <RuleChip key={entry.rule.id} entry={entry} archived={Boolean(space.archivedAt)}
    busy={changingRuleId === entry.rule.id} disabled={refreshing} onToggle={onToggle} />;
  return <article aria-labelledby={titleId} className="min-w-0 space-y-4 rounded-3xl border border-border-subtle bg-surface p-4 sm:p-5">
    <header className="flex items-start gap-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-surface-sunken"><Icon className="size-5" aria-hidden /></span>
      <div className="min-w-0 flex-1">
        <h2 id={titleId} className="break-words text-base font-bold">{space.name}</h2>
        <SpaceMembersSummary members={entry.members} />
        <p className="mt-1 break-words text-xs text-text-secondary">{SPACE_KIND_LABELS[space.kind]} · sua parte {space.myEffectivePct.toLocaleString('pt-BR')}% · {space.myRole === 'editor' ? 'editor' : 'somente leitura'}</p>
        <SpaceDates space={space} />
      </div>
      <Button asChild variant="ghost" size="sm"><Link to={spaceHref(space.id)} aria-label={`Abrir ${space.name}`}>Abrir</Link></Button>
    </header>
    <section aria-label={`Suas regras em ${space.name}`} className="space-y-3 rounded-2xl border border-border-subtle p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-xs font-semibold text-text-secondary">Suas regras de envio</h3>
        <span className="inline-flex items-center gap-1 rounded-full bg-surface-sunken px-2 py-1 text-2xs text-text-secondary"><LockSimpleIcon className="size-3" aria-hidden />só você vê</span>
      </div>
      <p className="text-xs text-text-secondary">Lançamentos seus que combinam com as regras entram automaticamente no espaço. Cada pessoa cria as próprias regras; as outras pessoas só veem o que entrou.</p>
      <SpaceRulesNotice space={space} />
      <ul className="flex flex-wrap gap-2" aria-label="Regras de envio">{rules.map(renderRule)}</ul>
      <EmptyRules count={rules.length} />
      <div className="flex items-start gap-2 rounded-xl bg-surface-sunken p-3 text-xs text-text-secondary">
        <ArrowsLeftRightIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
        <p>Regras novas nascem na <strong>lista de transações</strong>: envie um lançamento e marque “Daqui para frente, enviar sempre”. <Link to="/transactions" className="cursor-pointer font-semibold text-primary underline-offset-4 hover:underline">Ir para transações</Link></p>
      </div>
    </section>
    <SpaceRulesPending entry={entry} refreshing={refreshing} />
  </article>;
}

function SpaceMembersSummary({ members }: Readonly<{ members: SpaceRulesOverview['members'] }>) {
  const names = members.map((member) => member.name ?? 'Integrante').join(', ');
  return <p className="mt-1 break-words text-xs text-text-secondary">{names}</p>;
}

function SpaceDates({ space }: { space: SpaceSummary }) {
  const window = ruleWindow(space.startsAt, space.endsAt);
  if (!window) return null;
  return <p className="mt-1 text-xs text-text-secondary">{window}</p>;
}

function SpaceRulesNotice({ space }: { space: SpaceSummary }) {
  if (!space.archivedAt && space.myRole === 'editor') return null;
  return <p className="text-xs text-text-secondary">{space.archivedAt
    ? 'Espaço encerrado. Não recebe novos envios; você ainda pode pausar suas regras.'
    : 'Você tem acesso somente de leitura. As regras ficam salvas, mas só podem enviar quando você tiver permissão de editor.'}</p>;
}

function EmptyRules({ count }: { count: number }) {
  if (count) return null;
  return <p className="text-xs text-text-secondary">Nenhuma regra — só o que você enviar na mão entra.</p>;
}

function RuleChip({ entry, archived, busy, disabled, onToggle }: {
  entry: SpaceRuleOverview; archived: boolean; busy: boolean; disabled: boolean; onToggle: Props['onToggle'];
}) {
  const { rule } = entry;
  const label = ruleLabel(entry);
  const window = ruleWindow(rule.windowStart, rule.windowEnd);
  const handleToggle = () => { void onToggle(rule); };
  const Icon = rule.enabled ? PauseIcon : ArrowCounterClockwiseIcon;
  const action = `${rule.enabled ? 'Pausar' : 'Retomar'} ${label}`;
  return <li className="flex max-w-full items-center gap-1 rounded-xl border border-border-subtle pl-3 text-xs" aria-busy={busy}>
    <span className={cn('min-w-0 break-words py-2', !rule.enabled && 'text-text-secondary')}>
      <span className={cn(!rule.enabled && 'line-through')}>{label}</span>
      {window && <span className="block text-2xs text-text-secondary">{window}</span>}
      {!rule.enabled && <span className="block text-2xs text-text-secondary">Pausada</span>}
    </span>
    <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={handleToggle}
      disabled={disabled || (archived && !rule.enabled)} aria-label={action} title={action}>
      <Icon className="size-4" aria-hidden />
    </Button>
  </li>;
}
